import {
	getDeviceProductType,
	PRODUCT_TYPE_ALL,
	PRODUCT_TYPE_EVO,
	PRODUCT_TYPE_NOVA
} from '../product-filter';

const parseProductType = productType => {
	const parsedProductType = parseInt(productType);
	return parsedProductType === PRODUCT_TYPE_EVO || parsedProductType === PRODUCT_TYPE_NOVA
		? parsedProductType
		: PRODUCT_TYPE_ALL;
};

export const toRoiNumber = value => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : 0;
	}

	if (typeof value !== 'string') {
		return 0;
	}

	const normalizedValue = value.replace(/[^0-9.-]/g, '');
	const parsedValue = Number(normalizedValue);
	return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const getProcedureProductType = procedure => {
	return getDeviceProductType(procedure);
};

export const getProcedureRevenue = procedure => {
	const amount = toRoiNumber(procedure && procedure.Amount);
	const total = toRoiNumber(procedure && procedure.total);
	const price = toRoiNumber(procedure && procedure.Price);

	if (amount > 0) {
		return amount;
	}

	return total > 0 && price > 0 ? total * price : 0;
};

export const normalizeRoiProcedure = procedure => {
	const normalizedProcedure = procedure || {};

	return {
		...normalizedProcedure,
		CategoryID: parseInt(normalizedProcedure.CategoryID),
		ProductID: getProcedureProductType(normalizedProcedure),
		total: toRoiNumber(normalizedProcedure.total),
		Price: toRoiNumber(normalizedProcedure.Price),
		Goal: toRoiNumber(normalizedProcedure.Goal),
		Amount: getProcedureRevenue(normalizedProcedure)
	};
};

export const normalizeRoiProcedures = procedures => {
	return (Array.isArray(procedures) ? procedures : []).map(normalizeRoiProcedure);
};

const normalizeProcedureName = name => String(name || '').trim().toLowerCase();

export const getProcedurePresetID = procedure => {
	if (!procedure) {
		return null;
	}

	const procedureID = procedure.PresetProcedureID !== undefined && procedure.PresetProcedureID !== null
		? procedure.PresetProcedureID
		: procedure.preset !== undefined && procedure.preset !== null
			? procedure.preset
			: procedure.ProcedureID;
	const parsedProcedureID = parseInt(procedureID);

	return Number.isFinite(parsedProcedureID) ? parsedProcedureID : null;
};

const getProcedureMatchKeys = procedure => {
	const categoryID = parseInt(procedure && procedure.CategoryID);
	const procedureID = getProcedurePresetID(procedure);
	const procedureName = normalizeProcedureName(procedure && (procedure.Name || procedure.name));
	const keys = [];

	if (Number.isFinite(categoryID) && procedureID !== null) {
		keys.push(categoryID + ':id:' + procedureID);
	}

	if (Number.isFinite(categoryID) && procedureName) {
		keys.push(categoryID + ':name:' + procedureName);
	}

	return keys;
};

const indexProcedures = procedures => {
	const index = new Map();

	(Array.isArray(procedures) ? procedures : []).forEach(procedure => {
		getProcedureMatchKeys(procedure).forEach(key => {
			if (!index.has(key)) {
				index.set(key, procedure);
			}
		});
	});

	return index;
};

const findMatchingProcedure = (procedure, index) => {
	const keys = getProcedureMatchKeys(procedure);
	for (let i = 0; i < keys.length; i += 1) {
		if (index.has(keys[i])) {
			return index.get(keys[i]);
		}
	}

	return null;
};

export const getRevenueCategoryIDs = productType => {
	return parseProductType(productType) === PRODUCT_TYPE_NOVA ? [1, 2, 4, 5] : [1, 2, 3];
};

export const reconcileDeviceRevenue = ({
	roiProcedures,
	pricingProcedures,
	countProcedures,
	serialNumber,
	productType
}) => {
	const prices = Array.isArray(pricingProcedures) ? pricingProcedures : [];
	if (prices.length === 0) {
		return normalizeRoiProcedures(roiProcedures);
	}

	const counts = Array.isArray(countProcedures) ? countProcedures : [];
	const countIndex = indexProcedures(counts);
	const roiIndex = indexProcedures(roiProcedures);
	const reconciledKeys = new Set();
	const reconciledProcedures = prices.map(priceProcedure => {
		const countProcedure = findMatchingProcedure(priceProcedure, countIndex);
		const roiProcedure = findMatchingProcedure(priceProcedure, roiIndex);
		const matchingProcedure = countProcedure || roiProcedure || {};
		const total = toRoiNumber(matchingProcedure.total);
		const price = toRoiNumber(priceProcedure.Price);
		const categoryID = parseInt(priceProcedure.CategoryID);
		const procedureID = getProcedurePresetID(priceProcedure);
		const name = priceProcedure.Name || priceProcedure.name || matchingProcedure.Name || matchingProcedure.name;
		const normalizedProcedure = normalizeRoiProcedure({
			...roiProcedure,
			...priceProcedure,
			ProcedureID: procedureID,
			PresetProcedureID: procedureID,
			SerialNumber: serialNumber,
			ProductID: productType,
			CategoryID: categoryID,
			Name: name,
			total,
			Price: price,
			Goal: priceProcedure.Goal,
			Amount: total * price
		});

		getProcedureMatchKeys(normalizedProcedure).forEach(key => reconciledKeys.add(key));
		return normalizedProcedure;
	});

	counts.forEach(countProcedure => {
		if (getProcedureMatchKeys(countProcedure).some(key => reconciledKeys.has(key))) {
			return;
		}

		const roiProcedure = findMatchingProcedure(countProcedure, roiIndex) || {};
		const total = toRoiNumber(countProcedure.total);
		const price = toRoiNumber(roiProcedure.Price);
		reconciledProcedures.push(normalizeRoiProcedure({
			...roiProcedure,
			...countProcedure,
			ProcedureID: getProcedurePresetID(countProcedure),
			SerialNumber: serialNumber,
			ProductID: productType,
			Name: countProcedure.Name || countProcedure.name || roiProcedure.Name,
			total,
			Price: price,
			Amount: total * price
		}));
	});

	return reconciledProcedures;
};

export const filterDevicesByProductType = (devices, productType) => {
	const selectedProductType = parseProductType(productType);
	const serialNumbers = new Set();

	return (Array.isArray(devices) ? devices : []).filter(device => {
		if (!device || device.SerialNumber === undefined || device.SerialNumber === null) {
			return false;
		}

		const serialNumber = String(device.SerialNumber);
		if (!serialNumber || serialNumbers.has(serialNumber)) {
			return false;
		}

		if (selectedProductType !== PRODUCT_TYPE_ALL && getDeviceProductType(device) !== selectedProductType) {
			return false;
		}

		serialNumbers.add(serialNumber);
		return true;
	});
};

export const findDeviceBySerial = (devices, serialNumber) => {
	if (serialNumber === undefined || serialNumber === null) {
		return null;
	}

	const normalizedSerialNumber = String(serialNumber);
	return (Array.isArray(devices) ? devices : []).find(device => {
		return device && String(device.SerialNumber) === normalizedSerialNumber;
	}) || null;
};

export const getRoiRequestProductType = ({ headerProductType, selectedSerialNumber, devices, fallbackProductType }) => {
	const fallback = parseProductType(fallbackProductType);
	if (fallback !== PRODUCT_TYPE_ALL) {
		return fallback;
	}

	if (selectedSerialNumber !== undefined && selectedSerialNumber !== null) {
		const selectedDevice = findDeviceBySerial(devices, selectedSerialNumber);
		const deviceProductType = getDeviceProductType(selectedDevice);
		if (deviceProductType !== PRODUCT_TYPE_ALL) {
			return deviceProductType;
		}
	}

	return parseProductType(headerProductType);
};

export const getRoiEndpointName = ({ isDateRange, isAllDevices }) => {
	if (isAllDevices) {
		return isDateRange
			? 'return-on-investment-for-user-devices-date-range'
			: 'return-on-investment-for-user-devices';
	}

	return isDateRange
		? 'return-on-investment-for-user-device-date-range'
		: 'return-on-investment-for-user-device';
};

export const filterProceduresForCategory = (procedures, category) => {
	if (!category) {
		return [];
	}

	return (Array.isArray(procedures) ? procedures : []).filter(procedure => {
		if (parseInt(procedure.CategoryID) !== parseInt(category.categoryID)) {
			return false;
		}

		const procedureProductType = getProcedureProductType(procedure);
		return procedureProductType === PRODUCT_TYPE_ALL || procedureProductType === parseProductType(category.productID);
	});
};
